import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../api.service';
import { BrowserCacheLocation, Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { VersionService } from '../version/version.service';

export interface MsalApiConfig {
  clientId: string;
  authority: string;
  redirectUrl: string;
  logoutUrl: string;
}

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  public msalConfig!: Configuration;
  public pca!: PublicClientApplication;
  private apiConfig?: MsalApiConfig;
  private initialized = false; 
  private http: HttpClient;
  private configUrl: string;
  private versioning: string = Date.now().toString();

  constructor(private backend: HttpBackend, private apiService: ApiService, private versionService: VersionService) {
    this.http = new HttpClient(backend);
    this.configUrl = `${this.apiService.apiUrl}/auth/msal`;
  }

  public async loadMsalConfig(): Promise<void> {
    if (this.initialized) return;  // prevent multiple calls

    // 1️ - Load version
    const version = await firstValueFrom(
      this.http.get('assets/version.txt?v=' + this.versioning, { responseType: 'text' })
    );
    this.versionService.set(version.trim());

    // 2 -  Load cached config from localStorage
    if (!this.apiConfig) {
      const cached = localStorage.getItem('msalApiConfig');
      if (cached) this.apiConfig = JSON.parse(cached) as MsalApiConfig;
    }

    // Fetch from backend if not cached
    if (!this.apiConfig) {
      this.apiConfig = await firstValueFrom(this.http.get<MsalApiConfig>(this.configUrl));
      localStorage.setItem('msalApiConfig', JSON.stringify(this.apiConfig));
    }

    // Build MSAL config
    const originUri = window.location.origin;
    this.msalConfig = {
      auth: {
        clientId: this.apiConfig.clientId,
        authority: this.apiConfig.authority,
        redirectUri: originUri + '/auth/',
        navigateToLoginRequestUrl: false,
        postLogoutRedirectUri: originUri + '/logout/'
      },
      cache: { cacheLocation: BrowserCacheLocation.LocalStorage, storeAuthStateInCookie: false },
      system: { loggerOptions: { logLevel: LogLevel.Info, loggerCallback: (lvl, msg) => console.log(msg) } }
    };

    // Initialize PCA synchronously
    this.pca = new PublicClientApplication(this.msalConfig);

    this.initialized = true; // mark as initialized
    console.log('MSAL config ready');
  }
}
